if (!customElements.get('rumi-live-search')) {
  customElements.define('rumi-live-search', class extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.rumi-search-results');
      this.status = this.querySelector('[role="status"]');
      this.cache = new Map();
      this.version = 0;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.version++;
        this.request?.abort();
        this.close();
        if (this.input.value.trim()) this.timer = setTimeout(() => this.search(), 220);
        else this.status.textContent = '';
      }, options);
      this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.search(); }, options);
      this.input.addEventListener('keydown', event => this.keydown(event), options);
      this.addEventListener('keydown', event => { if (event.key === 'Escape') { this.close(); this.input.focus(); } }, options);
      this.addEventListener('focusout', () => setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close();
      }, 0), options);
      document.addEventListener('pointerdown', event => { if (!this.contains(event.target)) this.close(); }, options);
    }
    async search() {
      const query = this.input.value.trim();
      if (!query) return;
      this.request?.abort();
      this.request = new AbortController();
      const version = ++this.version;
      this.status.textContent = 'Searching products…';
      this.input.setAttribute('aria-busy', 'true');
      try {
        let markup = this.cache.get(query);
        if (markup === undefined) {
          const params = new URLSearchParams({ q: query, 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][unavailable_products]': 'hide', section_id: 'rumi-search-suggestions' });
          const response = await fetch(`${this.dataset.endpoint}?${params}`, { signal: this.request.signal });
          if (!response.ok) throw new Error('Search unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = doc.querySelector('[data-rumi-search-content]');
          if (!content) throw new Error('Missing search results');
          markup = content.innerHTML;
          if (this.cache.size > 30) this.cache.clear();
          this.cache.set(query, markup);
        }
        if (version !== this.version || query !== this.input.value.trim()) return;
        this.results.innerHTML = markup;
        this.items = [...this.results.querySelectorAll('[role="option"]')];
        this.items.forEach((item, index) => item.id = `${this.results.id}-${index}`);
        this.index = -1;
        this.status.textContent = `${this.results.querySelectorAll('.rumi-search-result').length} product suggestions`;
        if (this.contains(document.activeElement)) {
          this.results.hidden = false;
          this.input.setAttribute('aria-expanded', 'true');
        }
      } catch (error) {
        if (error.name !== 'AbortError' && version === this.version) {
          this.close();
          this.status.textContent = 'Suggestions unavailable. Press Enter to search all products.';
        }
      } finally {
        if (version === this.version) this.input.removeAttribute('aria-busy');
      }
    }
    keydown(event) {
      if (this.results.hidden) return;
      if (event.key === 'Enter' && this.index >= 0) {
        event.preventDefault();
        this.items[this.index].click();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        if (!this.items?.length) return;
        this.index = this.index < 0 ? (step > 0 ? 0 : this.items.length - 1) : (this.index + step + this.items.length) % this.items.length;
        this.items.forEach((item, index) => item.setAttribute('aria-selected', String(index === this.index)));
        this.input.setAttribute('aria-activedescendant', this.items[this.index].id);
        this.items[this.index].scrollIntoView({ block: 'nearest' });
      }
    }
    close() {
      clearTimeout(this.timer);
      this.version++;
      this.request?.abort();
      this.status.textContent = '';
      this.results.hidden = true;
      this.index = -1;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.removeAttribute('aria-busy');
      this.items?.forEach(item => item.setAttribute('aria-selected', 'false'));
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.version++;
      this.request?.abort();
      this.events.abort();
    }
  });
}
